import { OPS_AUTH, USE_MOCK_AUTH } from "../config/api";
import { apiRequest, unwrapApiEnvelope } from "./apiClient";
import { cleanGhanaPhone } from "../utils/ghanaPhone";
import { getOpsUserDisplayName, normalizeOpsUser } from "../utils/normalizeOpsUser";

const PROFILE_KEYS = ["first_name", "last_name", "email", "phone_number"];

function normalizeField(key, value) {
  const trimmed = String(value ?? "").trim();
  if (key === "email") return trimmed.toLowerCase();
  if (key === "phone_number") {
    return cleanGhanaPhone(trimmed) || trimmed.replace(/^\+/, "").replace(/\s+/g, "");
  }
  return trimmed;
}

/**
 * Build a partial payload with only fields that differ from the baseline.
 * @returns {Record<string, string>}
 */
export function buildProfilePatch(next, baseline = {}) {
  const patch = {};
  for (const key of PROFILE_KEYS) {
    const nextValue = normalizeField(key, next[key]);
    const baseValue = normalizeField(key, baseline[key]);
    if (nextValue !== baseValue) {
      patch[key] = nextValue;
    }
  }
  return patch;
}

function localUserPatch(patch, currentUser = {}) {
  const merged = {
    ...currentUser,
    ...patch,
  };
  return {
    ...patch,
    name: getOpsUserDisplayName(merged),
    phone: merged.phone_number != null ? String(merged.phone_number) : currentUser.phone,
  };
}

/**
 * Update the signed-in operator profile with only changed fields.
 * PUT /api/ops/profile — Bearer token required.
 *
 * @param {string} token
 * @param {object} nextForm
 * @param {object} baselineForm — current saved values
 */
export async function updateOpsProfile(token, nextForm, baselineForm = {}) {
  const body = buildProfilePatch(nextForm, baselineForm);

  if (!Object.keys(body).length) {
    throw new Error("No profile changes to save.");
  }

  if (USE_MOCK_AUTH) {
    await new Promise((r) => setTimeout(r, 500));
    return localUserPatch(body, baselineForm);
  }

  if (!token) {
    throw new Error("You must be signed in to update your profile.");
  }

  const json = await apiRequest(OPS_AUTH.profile, {
    method: "PUT",
    token,
    body,
  });

  const data = unwrapApiEnvelope(json);
  const normalized = normalizeOpsUser(data);

  if (normalized) {
    const fromApi = {};
    for (const key of PROFILE_KEYS) {
      if (body[key] != null || data?.[key] != null) {
        fromApi[key] = normalized[key];
      }
    }
    if (fromApi.first_name != null || fromApi.last_name != null) {
      fromApi.name = normalized.name;
    }
    if (fromApi.phone_number != null) {
      fromApi.phone = normalized.phone;
    }
    return Object.keys(fromApi).length ? fromApi : localUserPatch(body, baselineForm);
  }

  return localUserPatch(body, baselineForm);
}
